import { connect, provideComponent, navigateTo, Obj } from 'scrivito'

import { TimeTrackingTab } from './const'
import { useTimeTrackingUrlState } from './hooks/useTimeTrackingUrlState'
import { timeTrackingTabTitles } from './tabs'
import AllTimesTab from './tabs/allTimes/AllTimesTab'
import { MyOrdersTab } from './tabs/myOrders/MyOrdersTabComponent'
import { MyTeamTab } from './tabs/myTeam/MyTeamTabComponent'
import { MyTimesTab } from './tabs/myTimes/MyTimesTabComponent'
import { TimeTrackingOverviewWidget } from './TimeTrackingOverviewWidgetClass'
import { NavTabItem, NavTabs } from '@/Atomic/Templates/NavTabs/NavTabs'
import { Loading } from '@/Components/Loading'
import './TimeTrackingOverviewWidget.scss'
import { TimerecordTeam } from '@/Data/TimerecordTeam/TimerecordTeamDataClass'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import {
  useLogTimeDrawer,
  UseLogTimeDrawerReturn,
} from '@/hooks/useLogTimeDrawer'
import { ensureString } from '@/utils/ensureString'

interface TimeTrackingTabsInnerProps {
  logTimeDrawer: UseLogTimeDrawerReturn
  currentUserId: string
  isTeamLeader: boolean
}

const leaderOnlyTabs = new Set([TimeTrackingTab.MY_TEAM])

const TimeTrackingTabsInner = connect(
  ({
    logTimeDrawer,
    currentUserId,
    isTeamLeader,
  }: TimeTrackingTabsInnerProps) => {
    const { open: openLogTimeDrawer } = logTimeDrawer

    const {
      currentTab,
      myTimesRange,
      allTimesRange,
      teamMonthRange,
      ordersMonthRange,
      filters,
      setCurrentTab,
      navigateToTab,
      setMyTimesRange,
      setAllTimesRange,
      setTeamMonthRange,
      setOrdersMonthRange,
      setFilters,
    } = useTimeTrackingUrlState({ currentUserId, isTeamLeader })

    const navigateToResource = (
      dataItemId: string,
      permalink: string,
      idKey: string,
    ) => {
      navigateTo(() => Obj.getByPermalink(permalink), {
        [idKey]: dataItemId,
      })
    }

    const tabs: NavTabItem[] = timeTrackingTabTitles().map((tab) => ({
      id: tab.id,
      title: tab.title,
      disabled: !isTeamLeader && leaderOnlyTabs.has(tab.id as TimeTrackingTab),
    }))

    const handleTabChange = (id: string) => {
      if (!isTeamLeader && leaderOnlyTabs.has(id as TimeTrackingTab)) return
      setCurrentTab(id)
    }

    return (
      <>
        <NavTabs
          tabs={tabs}
          variant="large"
          activeTab={currentTab}
          onTabChange={handleTabChange}
          renderPanels={false}
        />

        <div className="tab-content">
          <div className="tab-pane fade active show">
            {currentTab === TimeTrackingTab.MY_TIMES && (
              <MyTimesTab
                openLogTimeDrawer={openLogTimeDrawer}
                navigateToTab={navigateToTab}
                navigateToResource={navigateToResource}
                initialFilters={filters}
                weekRange={myTimesRange}
                onWeekRangeChange={setMyTimesRange}
                onFilterChange={setFilters}
                currentUserId={currentUserId}
              />
            )}
            {currentTab === TimeTrackingTab.ALL_TIMES && (
              <AllTimesTab
                openLogTimeDrawer={openLogTimeDrawer}
                navigateToTab={navigateToTab}
                navigateToResource={navigateToResource}
                initialFilters={filters}
                weekRange={allTimesRange}
                onWeekRangeChange={setAllTimesRange}
                onFilterChange={setFilters}
                currentUserId={currentUserId}
              />
            )}
            {currentTab === TimeTrackingTab.MY_TEAM && (
              <MyTeamTab
                openLogTimeDrawer={openLogTimeDrawer}
                navigateToTab={navigateToTab}
                navigateToResource={navigateToResource}
                initialFilters={filters}
                monthRange={teamMonthRange}
                onMonthRangeChange={setTeamMonthRange}
                onFilterChange={setFilters}
                currentUserId={currentUserId}
              />
            )}
            {currentTab === TimeTrackingTab.MY_ORDERS && (
              <MyOrdersTab
                navigateToTab={navigateToTab}
                navigateToResource={navigateToResource}
                initialFilters={filters}
                monthRange={ordersMonthRange}
                onMonthRangeChange={setOrdersMonthRange}
                onFilterChange={setFilters}
                currentUserId={currentUserId}
              />
            )}
          </div>
        </div>
      </>
    )
  },
)

const TimeTrackingTabs = connect(() => {
  const currentUserId = ensureString(WhoAmI.get('contactId'))
  const isTeamLeader =
    TimerecordTeam.all()
      .transform({ filters: { leader: currentUserId } })
      .take().length > 0
  const logTimeDrawer = useLogTimeDrawer(currentUserId, isTeamLeader)
  return (
    <TimeTrackingTabsInner
      logTimeDrawer={logTimeDrawer}
      currentUserId={currentUserId}
      isTeamLeader={isTeamLeader}
    />
  )
})

provideComponent(
  TimeTrackingOverviewWidget,
  () => (
    <div className="time-tracking-overview">
      <TimeTrackingTabs />
    </div>
  ),
  { loading: Loading },
)
